/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import loader from 'assets/img/loader/tier-loader.json';
import Avatar from 'components/base/Avatar';
import { Modal, Spinner } from 'react-bootstrap';
import PinInput from 'react-pin-input';
import Backdrop from 'components/base/Backdrop';
import Lottie from 'lottie-react';
import { gsap } from 'gsap';
import styled from '@emotion/styled';
import Button from 'components/base/Button';
import paymentDoneImg from 'assets/img/illustration/paymentDone2.gif';
import loaderTier from 'assets/img/loader/tier-loader.gif';
import postData from 'services/postData';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/icon';
import { currencyFormat } from 'helpers/utils';
import AlertWithIcon from 'components/custom/modal/AlertWithIcon';

const CustomModal = styled(Modal)`
  background-color: #0000004b;
  backdrop-filter: blur(2px);
  & .modal-dialog {
    max-width: 300px !important;
    margin-right: auto;
    margin-left: auto;
  }
  & .modal-header .btn-close {
    background-color: #ffffff39;
  }
`;

const PaymentPinModal = ({
  show,
  handleClose,
  proceedPayment,
  resetData,
  amount,
  receiverName,
  driverName,
  isDifferentName,
  paymentMode,
  paymentCallback
}) => {
  const lottieRef = useRef();
  const [pinRef, setPinRef] = useState(false);
  const [pin, setPin] = useState('');
  const [isPinCorrect, setIsPinCorrect] = useState(true);
  const [paymentFail, setPaymentFail] = useState(false);
  const [paymentFailMessage, setPaymentFailMessage] =
    useState('Payment Failed');
  const [loading, setLoading] = useState(false);
  const [payment, setPaymentDone] = useState(false);
  const tl = gsap.timeline();
  const navigate = useNavigate();

  const goToPaymentList = () => {
    navigate('/payments/quick-payment-list');
  };

  const handlePayment = async () => {
    setLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async position => {
          const latitude = position.coords.latitude;
          const longitude = position.coords.longitude;

          proceedPayment({ pin, latitude, longitude }, response => {
            if (response.status) {
              setPaymentDone(true);
              setLoading(false);
            } else {
              if (response.message === 'Invalid Credential') {
                setIsPinCorrect(false);
              } else {
                setPaymentFail(true);
                setPaymentFailMessage(response.message);
              }
              setLoading(false);
              pinRef?.clear();
              setPin('');
            }
          });
        },
        error => {
          console.error('Error getting geolocation:', error);
          // Handle error scenario if needed
          setLoading(false);
          setPaymentFail(true);
          setPaymentFailMessage('Please allow location to proceed payment');
        }
      );
    } else {
      console.error('Geolocation is not supported by this browser.');
      // Handle scenario where geolocation is not supported
      setLoading(false);
      setPaymentFail(true);
      setPaymentFailMessage(
        'Geolocation is not supported by this browser. You can not able to proceed payment'
      );
    }
    // fadeOut('.pinContainer');
  };

  useEffect(() => {
    if (payment) {
      fadeIn('.successContain');
    }
  }, [payment]);

  const closeModal = () => {
    if (payment) {
      resetData();
      handleClose();
      tryAgain();
      if (paymentCallback) {
        paymentCallback(true);
      }
    } else {
      setLoading(false);
      handleClose();
      tryAgain();
    }
  };

  const fadeIn = className => {
    tl.fromTo(
      className,
      { marginLeft: '-600px' },
      { marginLeft: 0, duration: 0.5, ease: 'power2.out' },
      '+=0.5'
    );
  };

  const fadeOut = className => {
    tl.fromTo(
      className,
      { opacity: 1 },
      { opacity: 0, duration: 0.2, zIndex: '-1' }
    );
  };

  const tryAgain = () => {
    setPaymentFail(false);
    setPaymentDone(false);
    pinRef?.clear();
    setPin('');
  };

  const handleChangePin = val => {
    setPin(val);
    if (!isPinCorrect) {
      setIsPinCorrect(true);
    }
  };

  return (
    <CustomModal
      centered
      size="sm"
      dialogClassName="modal-300px"
      show={show}
      onHide={closeModal}
      backdrop="static"
      // style={{ overflow: 'hidden' }}
      keyboard={false}
    >
      <Modal.Header
        className="border-0 position-relative pb-0 end-0 dark__text-white"
        closeButton={!loading}
      ></Modal.Header>

      <Modal.Body className="pt-0 overflow-hidden position-relative">
        {!payment && !paymentFail ? (
          <div
            className="pinContainer"
            style={{ opacity: loading || payment ? 0 : 1 }}
          >
            <div className="d-flex align-items-center flex-column">
              <Avatar size="xl" variant="name">
                {paymentMode === 'Upi' ? receiverName?.[0] : driverName?.[0]}
              </Avatar>
              <h4 className="mt-2">{currencyFormat(amount)}</h4>
              <p className="fs-9">
                Pay to{' '}
                <i className="text-primary">
                  {paymentMode === 'Upi' ? receiverName : driverName}
                </i>{' '}
                via {paymentMode}
              </p>
              {isDifferentName && paymentMode === 'Upi' ? (
                <AlertWithIcon
                  variant={'soft-warning'}
                  icon={'fluent:warning-20-regular'}
                  iconProp={{ fontSize: '18px' }}
                  className="p-0 px-1"
                  body={
                    <small className="fs-10 fw-normal lh-base ">
                      <b>Warning: </b>
                      The driver's name ({driverName}) and UPI name (
                      {receiverName}) are different.
                    </small>
                  }
                />
              ) : null}
            </div>
            <div className="mt-2 d-flex align-items-center flex-column ">
              <h5>Insert Payment Pin</h5>
              <p className="fs-9 text-700 mb-1 text-center mb-1">
                Just insert the Pin and you're good to go.
              </p>
              <PinInput
                inputFocusStyle={{
                  outline: 0,
                  borderWidth: '2px',
                  padding: ' 0.05rem',
                  borderColor: '#004DFF'
                }}
                inputStyle={{
                  borderWidth: '1px',
                  lineHeight: '1.5',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  borderStyle: 'solid',
                  letterSpacing: '0.15px',
                  padding: '0.05rem',
                  backgroundColor: 'transparent',
                  //   color: '#fff',
                  fontFamily:
                    'Inter, sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
                  borderColor: '#757575',
                  WebkitTransition:
                    'border-color 300ms cubic-bezier(0.4, 0q 0.2, 1) 0ms, box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                  transition:
                    'border-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                  maxWidth: '40px',
                  textAlign: 'center',
                  height: '40px',
                  marginTop: '0.5rem',
                  marginBottom: '0.5rem'
                }}
                length={4}
                focus
                // disabled
                // secret
                className="text-white mx-auto"
                ref={p => setPinRef(p)}
                type="numeric"
                onChange={handleChangePin}
              />
              {!isPinCorrect ? (
                <small className="text-danger">Incorrect Pin</small>
              ) : null}
            </div>
          </div>
        ) : (
          <>
            {paymentFail ? (
              <div className="d-flex flex-column justify-content-center align-items-center py-3 text-danger">
                <Icon
                  icon={'line-md:close-circle-twotone'}
                  fontSize={'120px'}
                />
                <p className="pb-0 mb-0 fs-7 text-danger text-center">
                  Payment Failed
                </p>
                <p className="pb-0 fs-9 text-danger text-center">
                  {paymentFailMessage}
                </p>
              </div>
            ) : (
              <div className="d-flex flex-column justify-content-center align-items-center py-3 successContain">
                <img
                  src={paymentDoneImg}
                  alt=""
                  height={'200px'}
                  width={'200px'}
                />
                <p className="pb-0 fs-7 text-primary">Payment Success</p>
              </div>
            )}
          </>
        )}

        <Backdrop show={loading} style={{ backgroundColor: 'transparent' }}>
          {/* <Lottie
          ref={lottieRef}
          style={{
            with: '100%',
            height: '100px'
          }}
          // lottieRef={lottieRef}
          animationData={loader}
          loop={true}
        /> */}
          <img src={loaderTier} alt="" width={'200px'} />
        </Backdrop>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        {/* <Button variant="phoenix-secondary" onClick={handleClose}>
          Cancel
        </Button> */}
        {!payment && !paymentFail ? (
          <Button
            loading={loading}
            disabled={pin?.length !== 4 || loading}
            loadingPosition="end"
            className="w-100 d-flex justify-content-center"
            variant="primary"
            onClick={handlePayment}
          >
            Pay
          </Button>
        ) : (
          <>
            {paymentFail ? (
              <Button className="w-100" variant="danger" onClick={tryAgain}>
                Try Again
              </Button>
            ) : (
              <Button
                className="w-100"
                variant="primary"
                onClick={paymentCallback ? closeModal : goToPaymentList}
              >
                Done
              </Button>
            )}
          </>
        )}
      </Modal.Footer>
    </CustomModal>
  );
};

export default PaymentPinModal;
